import { KDialog, KSelect, KSwitch, KTextInput } from "@kyburz/kdesign-system";
import { makeStyles } from "@material-ui/core";
import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import useSetState from "../../../../hooks/useTypedSetState";
import {
  BillingTariffDataInterface,
  BillingTariffInterface,
} from "../../../../interfaces/BillingTarifInterface";
import { simStatuses, simTariffs } from "../constants";
import { setAlert } from "../../../../redux/actions/actions";
import { updateBillingTariff } from "../../../../apiCalls/admin/billing_tariffs";

const useStyles = makeStyles(() => ({
  container: {
    display: "flex",
    flexDirection: "row",
    gap: 20,
  },
}));

interface Props {
  open: boolean;
  tariff: BillingTariffInterface;
  handleClose: (withReload: boolean) => void;
}

const EditBillingTariff = ({ open, tariff, handleClose }: Props) => {
  const dispatch = useDispatch();
  const company = useSelector(
    (state: any) => state.customer.currentCustomer?.shortname,
  );
  const classes = useStyles();

  const [{ _id, license, companyData, loading }, setState] = useSetState({
    ...tariff,
    companyData: tariff.companyData ?? ({} as BillingTariffDataInterface),
    loading: false,
  });

  const isSubmitDisabled =
    !companyData?.name?.length ||
    !license ||
    license < 0 ||
    companyData?.cost < 0 ||
    loading;

  useEffect(() => {
    setState({
      ...tariff,
      companyData: tariff.companyData ?? ({} as BillingTariffDataInterface),
      loading: false,
    });
  }, [tariff]);

  const handleBaseChange = (e: any) =>
    setState({ [e.target.name]: e.target.value });

  const handleCompanyDataChange = (e: any) =>
    setState({
      companyData: {
        ...(companyData || ({} as BillingTariffDataInterface)),
        [e.target.name]: e.target.value,
      },
    });

  const handleCheckboxChange = () =>
    setState({
      companyData: {
        ...(companyData || ({} as BillingTariffDataInterface)),
        enabled: !companyData?.enabled,
      },
    });

  const handleSelect = (value, fieldName) => {
    if (!fieldName) return;
    setState({
      companyData: {
        ...(companyData || ({} as BillingTariffDataInterface)),
        [fieldName]: value?.value,
      },
    });
  };

  const closePopup = (reload: any) => {
    handleClose(reload);
  };

  const onSubmit = () => {
    setState({ loading: true });
    updateBillingTariff({
      ...tariff,
      _id,
      license,
      overrides: {
        ...tariff.overrides,
        [company]: companyData,
      },
    })
      .then((res: any) => {
        if (res.data) {
          closePopup(true);
        } else {
          dispatch(
            setAlert(
              res.detail.details ||
                res.detail?.errors?.map((x: any) => (
                  <div>{JSON.stringify(x)}</div>
                )),
              "error",
            ),
          );
        }
      })
      .finally(() => setState({ loading: false }));
  };

  return (
    <KDialog
      open={open}
      title="Update billing tariff"
      size="sm"
      onClose={() => closePopup(false)}
      primaryCTAonClick={onSubmit}
      primaryCTAText="Update"
      primaryCTAColor="primary"
      secondaryCTAonClick={() => closePopup(false)}
      secondaryCTAText="Cancel"
      hasOverflow
      disabled={isSubmitDisabled}
      contentClassName={classes.container}
    >
      <div>
        <KTextInput
          label="Name"
          placeholder="Name"
          value={companyData?.name || ""}
          name="name"
          onChange={handleCompanyDataChange}
        />
        <KTextInput
          label="License"
          placeholder="License"
          value={license}
          name="license"
          onChange={handleBaseChange}
          type="number"
        />
      </div>
      <div>
        <KTextInput
          label="Cost"
          placeholder="Cost"
          value={companyData?.cost}
          name="cost"
          onChange={handleCompanyDataChange}
          type="number"
        />
        <KSelect
          label="SIM Tariff (for Telefonica API)"
          value={
            simTariffs.find(({ value }) => value === companyData?.sim_tariff)
              ?.label || "n.a."
          }
          onSelect={handleSelect}
          elements={simTariffs}
          name="sim_tariff"
          height={150}
          isClearable
        />
        <KSelect
          label="SIM Status (for Telefonica API)"
          value={
            simStatuses.find(({ value }) => value === companyData?.sim_status)
              ?.label || "n.a."
          }
          onSelect={handleSelect}
          elements={simStatuses}
          name="sim_status"
          height={75}
          isClearable
        />
        <KSwitch
          key="billing_tariff_edit"
          checked={companyData?.enabled}
          name="enabled"
          label="Enabled"
          onChange={handleCheckboxChange}
        />
      </div>
    </KDialog>
  );
};

export default EditBillingTariff;
